import * as Store from '../../../store';
import * as React from 'react';
import * as monaco from 'monaco-editor';
import {AutoSizer} from 'react-virtualized';
import {connect} from 'react-redux';
import {IAppContext, withAppContext} from '../../../AppContext';
import * as EditorWorker from 'worker-loader!monaco-editor/esm/vs/editor/editor.worker'; // eslint-disable-line import/no-webpack-loader-syntax
import s from './SQLEditor.module.scss';
import { EditorView, basicSetup } from "codemirror";
import {EditorState} from "@codemirror/state"
import { keymap } from "@codemirror/view";
import {sql} from "@codemirror/lang-sql"
import {indentService} from "@codemirror/language"
import { indentWithTab } from "@codemirror/commands";
import {autocompletion, completionStatus, acceptCompletion} from '@codemirror/autocomplete';
import { darcula } from '@uiw/codemirror-theme-darcula';


/* tslint:disable */
(window as any)["MonacoEnvironment"] = {
    getWorker: function (_moduleId: string, label: string) {
        switch (label) {
            case "editorWorkerService":
                return new EditorWorker();
            default:
                // TODO(ankoh) log warning to application log
                console.log("Monaco requested worker with unsupported label '" + label + "'")
                return null;
        }
    }
}

/* tslint:enable */

interface ISQLEditorProps {
    appContext: IAppContext;
    value: string,
    storeValue: (value: string) => void,
    theme: string,
}

class SQLEditor extends React.Component<ISQLEditorProps> {
    // The monaco container
    protected monacoContainer: HTMLDivElement | null;
    // The current value
    protected currentValue: string | null;
    // Prevent rendering multiple times before mounting
    protected ignoreUpdate: boolean;
    // The editor
    protected editor: EditorView | null;


    // Pending editor resize
    protected pendingEditorResize: number | null;

    constructor(props: ISQLEditorProps) {
        super(props);
        this.monacoContainer = null;
        this.currentValue = null;
        this.ignoreUpdate = false;
        this.editor = null;
        this.pendingEditorResize = null;
    }

    public componentDidMount() {
        this.initMonaco();
    }

    public componentDidUpdate(_prevProps: ISQLEditorProps) {
        // Editor not set?
        if (!this.editor) {
            return;
        }

        // Ignore update?
        if (this.ignoreUpdate) {
            return;
        }

        // Value changed?
        if (this.props.value !== this.currentValue) {
            // Always refer to the latest value
            this.currentValue = this.props.value || "";
            // Consider the situation of rendering 1+ times before the editor mounted
            if (this.editor) {
                this.ignoreUpdate = true;
                this.editor.dispatch(
                    {changes: [{from: 0, to: this.editor.state.doc.length, insert: this.currentValue}]}
                );
                this.ignoreUpdate = false;
            }
        }
    }

    public componentWillUnmount() {
        this.destroyMonaco();
    }

    public initMonaco() {
        // Get URL query parameter
        const q_arg = (new URLSearchParams(window.location.search)).get("q");
        if (q_arg) {
            this.props.storeValue(q_arg);
        }

        if (this.monacoContainer) {
            const startState = EditorState.create({
                doc: this.props.value,
                extensions: [
                    keymap.of([
                        {
                            key: 'Ctrl-Enter',
                            run: (view) => {
                                this.props.appContext.controller.startStopQuery();
                                return true;
                            }
                        }
                    ]),
                    basicSetup,
                    keymap.of([
                        {
                            key: "Tab",
                            run: (view: EditorView) => {
                                const status = completionStatus(view.state);

                                // If completion is active, accept the current suggestion
                                if (status === "active") {
                                    return acceptCompletion(view); // Accept autocomplete suggestion
                                }

                                // If no completion is active, indent with Tab
                                return false; // Otherwise, indent
                            }
                        },
                        indentWithTab
                    ]),
                    sql(),
                    autocompletion(),
                    EditorView.updateListener.of(update => {
                        if (update.docChanged) {
                            const updatedContent = this.editor!.state.doc.toString();
                            this.currentValue = updatedContent;
                            this.props.storeValue(updatedContent);
                        }
                    }),
                    indentService.of((context, pos) => {
                        const previousLine = context.lineAt(pos, -1)
                        return previousLine!.text!.match(/^(\s)*/)![0].length
                    }),
                    darcula
                ],

            });

            this.editor = new EditorView({
                state: startState,
                parent: this.monacoContainer
            });
        }
    }

    public destroyMonaco() {
        if (this.editor !== null) {
            this.editor.destroy();
        }
    }

    public render() {
        return (
            <div className={s.SQLEditor}>
                <div
                    className={s.monacoEditor}
                    ref={(ref) => this.monacoContainer = ref}
                />
            </div>
        );
    }
}

function mapStateToSQLEditorProps(state: Store.RootState) {
    return {
        value: state.labQueryText,
        theme: state.labTheme
    };
}

function mapDispatchToSQLEditorProps(dispatch: Store.Dispatch) {
    return {
        storeValue: (value: string) => (dispatch(Store.updateQueryText(value))),
    };
}

export default withAppContext(connect(mapStateToSQLEditorProps, mapDispatchToSQLEditorProps)(SQLEditor));

